import { Link, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { FormattedDate } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { select } from '@swf/core/store';
import type { ChestAsset } from '@swf/core/types/ChestInventory';
import { ChestLockedType } from '@swf/core/types/Notification';

import ConfigurableChest from '../ConfigurableChest/ConfigurableChest';
import Hourglass from '../assets/hourglass.svg';
import styles from '../shared.scss';

import { TXTCLI_Available, TXTCLI_Expires } from './RegularChest.messages.json';

interface Props {
  chestId: number;
  chestText: string;
  chestType: string;
  isAvailable: boolean;
  tierId: number;
  assets: ChestAsset[];
  customSkin?: string;
  availableOn?: string;
  expiresOn?: string;
  logoName?: string;
  lockType?: string;
  hideExpiryTime?: boolean;
  starsCrmCdnBaseUrl?: string;
}

const timeOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
};

const RegularChest: React.FC<Props> = ({
  chestId,
  chestText,
  chestType,
  isAvailable,
  tierId,
  assets,
  customSkin,
  availableOn,
  expiresOn,
  logoName,
  lockType,
  hideExpiryTime,
  starsCrmCdnBaseUrl
}) => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const history = useHistory();

  const openChest = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (!isAvailable) return;

    if (isEmbedded) {
      window.starsweb?.app.execute(`starsrewards/chest/${chestId}`);
    } else {
      history.replace('/account/open-chest', { chest: chestId });
    }
  };

  return (
    <Link
      className={classNames(styles.tile, { [styles.inactive]: !isAvailable })}
      onClick={openChest}
    >
      {!isAvailable && lockType === ChestLockedType.TimeLocked && (
        <div className={styles.icon}>
          <Hourglass />
        </div>
      )}
      <span className={styles.chestName}>{chestText}</span>
      <ConfigurableChest
        assets={assets}
        chestType={chestType}
        customSkin={customSkin}
        logoName={logoName}
        tierId={tierId}
        starsCrmCdnBaseUrl={starsCrmCdnBaseUrl}
      />
      <small className={styles.chestAvailable}>
        {isAvailable ? (
          !hideExpiryTime && <Translation {...TXTCLI_Expires} />
        ) : (
          <Translation {...TXTCLI_Available} />
        )}
        <span>
          {isAvailable ? (
            !hideExpiryTime && <FormattedDate {...timeOptions} value={expiresOn as string} />
          ) : (
            <FormattedDate {...timeOptions} value={availableOn as string} />
          )}
        </span>
      </small>
    </Link>
  );
};

export default RegularChest;
